import React, { useEffect, useState } from "react";
import HeroSectionV2 from "../../../components/HeroSectionV2";
import VolunteerGroupModal from "./VolunteerGroupModal";
import VolunteerGroupList from "./VolunteerGroupList";
import { getAuthHeaders } from "../../../api/authHelper";
import "./VolunteerGroup.css";

import Billede1 from "../../../Images/Billede1.jpg";

interface FunctionDto {
    functionId: number;
    functionName: string;
    eventID: number;
}

interface VolunteerGroup {
    id?: number;
    name: string;
    type: string;

    eventId: number;
    event?: {
        eventID: number;
        eventName: string;
    };

    function?: string;
    maxSlots: number;

    contactPersonId?: number;
    contactPerson?: {
        id: number;
        firstName: string;
        lastName: string;
    };

    selfAssignShifts: boolean;
    deadline?: string;
}

interface EventDto {
    eventID: number;
    eventName: string;
}

const VolunteerGroups: React.FC = () => {
    const apiUrl = process.env.REACT_APP_API_URL!;
    const [functions, setFunctions] = useState<FunctionDto[]>([]);
    const [groups, setGroups] = useState<VolunteerGroup[]>([]);
    const [events, setEvents] = useState<EventDto[]>([]);
    const [persons, setPersons] = useState<any[]>([]);
    const [showModal, setShowModal] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);

    const [form, setForm] = useState<VolunteerGroup>({
        name: "",
        type: "",
        eventId: 0,
        function: "",
        maxSlots: 0,
        contactPersonId: undefined,
        selfAssignShifts: false,
        deadline: "",
    });

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            const [groupsRes, eventsRes, personsRes, functionsRes] = await Promise.all([
                fetch(`${apiUrl}/api/VolunteerGroups`, { headers: getAuthHeaders() }),
                fetch(`${apiUrl}/api/events`, { headers: getAuthHeaders() }),
                fetch(`${apiUrl}/api/VolunteerGroups/all-persons`, {
                    headers: getAuthHeaders()
                }),
                fetch(`${apiUrl}/api/Function`, { headers: getAuthHeaders() }),
            ]);

            const groupsData = await groupsRes.json();
            const eventsData = await eventsRes.json();
            const personsData = await personsRes.json();
            const functionsData = await functionsRes.json();

            const groupsList = groupsData?.$values ?? groupsData ?? [];
            const eventsList = eventsData?.$values ?? eventsData ?? [];
            const functionsList = functionsData?.$values ?? functionsData ?? [];

            // persons endpoint returns ARRAY (no paging wrapper)
            const personsList = Array.isArray(personsData)
                ? personsData
                : personsData?.items ?? personsData?.$values ?? [];

            setGroups(groupsList);
            setEvents(eventsList);
            setFunctions(functionsList);
            setPersons(personsList);

        } catch (err) {
            console.error("loadData error:", err);
        }
    };

    const resetForm = () => {
        setEditingId(null);
        setForm({
            name: "",
            type: "",
            eventId: 0,
            function: "",
            maxSlots: 0,
            contactPersonId: undefined,
            selfAssignShifts: false,
            deadline: "",
        });
    };

    const saveGroup = async () => {
        try {
            const isEdit = editingId !== null;

            const url = isEdit
                ? `${apiUrl}/api/VolunteerGroups/${editingId}`
                : `${apiUrl}/api/VolunteerGroups`;

            const method = isEdit ? "PUT" : "POST";

            const payload = {
                name: form.name,
                type: form.type,
                eventId: form.eventId,
                function: form.function,
                maxSlots: form.maxSlots,
                contactPersonId: form.contactPersonId,
                selfAssignShifts: form.selfAssignShifts,
                deadline: form.deadline,
            };

            const res = await fetch(url, {
                method,
                headers: {
                    ...getAuthHeaders(),
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(payload),
            });

            if (!res.ok) {
                const err = await res.text();
                console.error(err);
                alert("Fejl ved gemning af gruppe");
                return;
            }

            // ✅ ONLY for CREATE (important!)
            if (!isEdit) {
                // backend returns the created group
                const createdGroup = await res.json();

                await fetch(
                    `${apiUrl}/api/VolunteerGroups/${createdGroup.id}/generate-persons`,
                    {
                        method: "POST",
                        headers: getAuthHeaders(),
                    }
                );
            }

            await loadData();
            resetForm();
            setShowModal(false);
        } catch (err) {
            console.error(err);
        }
    };

    const editGroup = (group: VolunteerGroup) => {
        setEditingId(group.id ?? null);

        setForm({
            name: group.name,
            type: group.type,
            eventId: group.eventId,
            function: group.function ?? "",
            maxSlots: group.maxSlots,
            contactPersonId: group.contactPersonId,
            selfAssignShifts: group.selfAssignShifts,
            deadline: group.deadline ?? "",
        });

        setShowModal(true);
    };

    const deleteGroup = async (id?: number) => {
        if (!id) return;
        if (!window.confirm("Slet gruppe?")) return;

        await fetch(`${apiUrl}/api/VolunteerGroups/${id}`, {
            method: "DELETE",
            headers: getAuthHeaders(),
        });

        await loadData();
    };

    return (
        <div>
            <HeroSectionV2
                titleEn="Volunteer Groups"
                titleDa="Frivilliggrupper"
                descriptionEn="Manage volunteer groups and assignments"
                descriptionDa="Opret, redigér og administrér frivilliggrupper"
                imageSrc={Billede1}
            />

            <div className="persons-container">

                <button
                    className="edit-btn"
                    onClick={() => {
                        resetForm();
                        setShowModal(true);
                    }}
                >
                    + Opret gruppe
                </button>

                <VolunteerGroupModal
                    open={showModal}
                    form={form}
                    events={events}
                    persons={persons}
                    functions={functions}
                    editingId={editingId}
                    setForm={setForm}
                    onClose={() => setShowModal(false)}
                    onSave={saveGroup}
                />

                <VolunteerGroupList
                    groups={groups}
                    persons={persons}
                    onEdit={editGroup}
                    onDelete={deleteGroup}
                />
            </div>
        </div>
    );
};

export default VolunteerGroups;